import React, {useEffect} from 'react';
import Layout from '../../components/Layout';
import PageToolBar from '../../components/PageToolBar';
import config from '../../step-config';
import usePageData from '../../../../hooks/usePageData';
import {StepStatus, useAppContext} from '../../context/app-context';
import {useForm, FormProvider} from 'react-hook-form';
import {PayrollCompanySetting} from '../../api/types';
import Banks from './components/banks';
import InsuranceApplicantUnit from './components/insurance-applicant-unit';
import IncomeTax from './components/income-tax';
import {useAuthContext, useClient} from '../../context/auth-context';
import {getData, useConfirmMutate, useSaveMutate} from '../../api/payroll';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IconButton} from '../../../../components/Button';
import {
  FailDialog,
  ResetDialog,
  ResetSuccessDialog,
} from '../../components/Dialog';
import {toast} from 'react-toastify';
import {useMutation} from 'react-query';

const defaultValues = {
  banks: [],
  incomeTaxRegistrationUnits: [],
  insuranceUnits: [],
};

export default function PayrollParameterSetting() {
  const client = useClient();
  const {companyId} = useAuthContext();
  const [showFailDialog, setShowFailDialog] = React.useState(false);
  const [showResetDialog, setShowResetDialog] = React.useState(false);
  const [showResetSuccessDialog, setShowResetSuccessDialog] =
    React.useState(false);
  const {data, init, status, remove, edit, updatedTime} =
    usePageData<any>('payroll-parameter-setting', {});
  const {
    changeStep,
    step,
    setIsLoading,
    isLoading,
    save,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();
  const methods = useForm<PayrollCompanySetting>({
    defaultValues,
  });
  const {
    reset,
    handleSubmit,
    getValues,
    formState: {isSubmitting},
  } = methods;

  const {mutateAsync: saveMutate} = useSaveMutate({
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const saveData = (data: PayrollCompanySetting) =>  {
    return client.put(`payroll/company/${companyId}/company/setting`, {
      data
    });
  }
  
  const confirmData = () => {
    return client.put(`payroll/company/${companyId}/company/setting/confirm`);
  }

  const {mutateAsync: handleConfirm} = useMutation(
    (data: PayrollCompanySetting) => saveData(data)
    .then(() => refetchEnablePY('next', step + 1))
    .then(async (company) => {
      try {
        if (company.enablePY) {
          await confirmData();
          setIsLoading(false)
          confirm();
          changeStep(step + 1);
          remove();
        } else {
          changeStep(11);
          remove();
        }
      } catch (e: any) {
        throw new Error(e);
      }
    }),
    {
      onMutate: () => setIsLoading(true),
      onError: (error: {code: number; message: string}) => {
        setIsLoading(false)
        if (error.code !== 500) {
          toast.error(error.message || '儲存失敗', {
            hideProgressBar: true,
            position: 'top-center',
            autoClose: false,
          });
        }
      },
    },
  );

  useEffect(() => {
    if (data === null || status === StepStatus.init) {
      setIsLoading(true);
      getData(client, companyId).then(d => {
        init(d.data || defaultValues);
        reset(d.data || defaultValues);
        setIsLoading(false);
      });
      return;
    }
    reset(data);
  }, []);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      const form_data = getValues();
      form_data.banks = form_data.banks.map((b: any) => {
        b.transferFileType = 0;
        return b;
      });
      saveMutate(form_data)
    }
  }, [saveAndGoStep])

  return (
    <React.Fragment>
      <Layout.MainContent>
        <PageToolBar pageTitle={config.payroll.steps.company.name}>
          <IconButton
            onClick={() => setShowResetDialog(true)}
            className={'reset-btn'}
          >
            <FontAwesomeIcon icon={['fal', 'redo']} />
            全部重設
          </IconButton>
        </PageToolBar>
        <FormProvider {...methods}>
          <form onChange={() => edit(getValues())}>
            <Banks />
            <InsuranceApplicantUnit />
            <IncomeTax />
          </form>
        </FormProvider>
        <ResetDialog
          isOpen={showResetDialog}
          onCancel={() => setShowResetDialog(false)}
          onConfirm={() => {
            edit(defaultValues);
            reset(defaultValues);
            setShowResetDialog(false);
            setShowResetSuccessDialog(true);
          }}
        />
        <FailDialog
          isOpen={showFailDialog}
          onConfirm={() => setShowFailDialog(false)}
          onCancel={() => setShowFailDialog(false)}
        />
        <ResetSuccessDialog
          isOpen={showResetSuccessDialog}
          onCancel={() => setShowResetSuccessDialog(false)}
        />
      </Layout.MainContent>
      <Layout.Footer
        updatedTime={updatedTime}
        showNextButton
        showPrevButton
        showSaveButton
        showUpdatedTime
        disabledSaveButton={status !== StepStatus.edit || isLoading}
        disabledPrevButton={status === StepStatus.edit || isLoading}
        disabledNextButton={isLoading}
        onSave={async () => {
          const form_data = getValues();
          try {
            edit(form_data);
            form_data.banks = form_data.banks.map((b: any) => {
              b.transferFileType = 0;
              return b;
            });
            await saveMutate(form_data);
          } catch (e) {}
        }}
        onNext={handleSubmit(
          async from_data => {
            try {
              from_data.banks = from_data.banks.map((b: any) => {
                b.transferFileType = 0;
                return b;
              });
              await handleConfirm(from_data);
            } catch (e) {}
          },
          () => {
            setShowFailDialog(true);
          },
        )}
        onPrev={async () => {
          await onPrev();
          remove();
        }}
      />
    </React.Fragment>
  );
}
